import { apiBase, fetchWithAuth } from "@/api/rest/core/apiClient";
import { readTextStream } from "@/api/rest/core/readTextStream";
import { Question } from "@/api/rest/models/question";

import i18n from "../../../i18n";

export enum EmitterDataTag {
  DOCUMENTS = "DOCUMENTS",
}

export enum ChatStreamEventType {
  SHOW_WARRANTY_FLOW_CTA = "SHOW_WARRANTY_FLOW_CTA",
}

export interface EmitterData {
  tag: EmitterDataTag;
  data: string;
}

export interface WarrantyFlowCtaPayload {
  targetPath: string;
  reason: string;
  suggestedTitle?: string;
  prefillSummary?: string;
}

export interface ChatStreamEvent {
  type: ChatStreamEventType;
  payload: WarrantyFlowCtaPayload;
}

export interface ParsedChatStreamText {
  text: string;
  tags: EmitterData[];
  events: ChatStreamEvent[];
}

/**
 * Represents a single message in the chat history sent to the backend.
 * Mirrors the backend's ChatMessageDto.
 */
export interface ApiChatMessage {
  /**
   * The role of the message sender ("user" or "assistant").
   */
  role: "user" | "assistant";
  /**
   * The content of the message.
   */
  content: string;
}

export interface EscalationOption {
  name: string;
  description: string;
}

export interface EscalateResponse {
  escalationOptions: EscalationOption[];
  title: string;
  escalationSummary: string;
  escalationCategory: string;
}

export interface SendEscalationRequest {
  householdId: number;
  accountId: number;
  history: ApiChatMessage[];
  category: string;
  title: string;
  contentText: string;
}

// Create a regex pattern from the enum values
const getEmitterTagPattern = (): string => {
  return Object.values(EmitterDataTag).join("|");
};

// Tags format: [tag:data] - only match specific emitter tags from the enum
const getEmitterRegex = (): RegExp => {
  const tagPattern = getEmitterTagPattern();
  return new RegExp(`\\[(${tagPattern}):([-\\w,]+)\\]`, "g");
};

export const askQuestion = async (
  householdId: number,
  question: string,
  history: ApiChatMessage[],
  streamResponse: (
    response: string,
    isComplete: boolean,
    tagsFound: EmitterData[],
    eventsFound: ChatStreamEvent[],
  ) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const result = await fetchWithAuth(`${apiBase}/chat/v1/question`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      question,
      householdId,
      history,
    }),
    signal,
  });
  if (!result.ok) {
    throw new Error(`Chat request failed: ${result.status}`);
  }
  if (!result.body) {
    throw i18n.t("common.error.500");
  }

  // The fetch signal also aborts the body: reading then rejects with AbortError.
  let rawResponse = "";
  await readTextStream(result.body, (text) => {
    rawResponse += text;
    const parsed = parseChatStreamText(rawResponse);
    streamResponse(parsed.text, false, parsed.tags, parsed.events);
  });

  const parsed = parseChatStreamText(rawResponse);
  streamResponse(parsed.text, true, parsed.tags, parsed.events);
};

export const escalateChat = async (householdId: number, history: ApiChatMessage[]): Promise<EscalateResponse> => {
  const response = await fetchWithAuth(`${apiBase}/chat/v1/escalate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      householdId,
      history,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to escalate chat");
  }

  return response.json();
};

export const sendEscalation = async (request: SendEscalationRequest): Promise<Question> => {
  const body = {
    householdId: request.householdId,
    accountId: request.accountId,
    category: request.category,
    title: request.title,
    contentText: request.contentText,
    history: request.history,
  };
  const response = await fetchWithAuth(`${apiBase}/api/v1/question-escalations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error("Failed to send escalation");
  }
  return response.json();
};

const getEmitterData = (response: string): EmitterData[] => {
  const emitterRegex = getEmitterRegex();
  const matches = [...response.matchAll(emitterRegex)];
  return matches.map(([, tag, data]) => ({
    tag: tag as EmitterDataTag,
    data: data,
  }));
};

const CHAT_STREAM_EVENT_PREFIX = "[[DOBU_CHAT_EVENT:";
const CHAT_STREAM_EVENT_SUFFIX = "]]";
const CHAT_STREAM_EVENT_REGEX = /\[\[DOBU_CHAT_EVENT:([A-Za-z0-9_-]+)\]\]/g;

export const parseChatStreamText = (response: string): ParsedChatStreamText => {
  return {
    text: cleanResponseText(response),
    tags: getEmitterData(response),
    events: getChatStreamEvents(response),
  };
};

const getChatStreamEvents = (response: string): ChatStreamEvent[] => {
  const matches = [...response.matchAll(CHAT_STREAM_EVENT_REGEX)];
  return matches
    .map(([, encoded]) => decodeChatStreamEvent(encoded))
    .filter((it): it is ChatStreamEvent => it !== null);
};

const decodeChatStreamEvent = (encoded: string): ChatStreamEvent | null => {
  try {
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const binary = atob(paddedBase64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));

    if (
      parsed?.type === ChatStreamEventType.SHOW_WARRANTY_FLOW_CTA &&
      typeof parsed.payload?.targetPath === "string" &&
      typeof parsed.payload?.reason === "string"
    ) {
      return parsed as ChatStreamEvent;
    }
  } catch (error) {
    console.warn("Failed to parse chat stream event:", error);
  }

  return null;
};

// Helper function to remove only emitter tags from response text
const cleanResponseText = (text: string): string => {
  const emitterRegex = getEmitterRegex();
  const withoutLegacyTags = text.replace(emitterRegex, "");
  const withoutCompleteEvents = withoutLegacyTags.replace(CHAT_STREAM_EVENT_REGEX, "");
  const incompleteEventStart = withoutCompleteEvents.lastIndexOf(CHAT_STREAM_EVENT_PREFIX);

  if (incompleteEventStart === -1) {
    return hideIncompleteTrailingTag(withoutCompleteEvents);
  }

  const suffixAfterStart = withoutCompleteEvents.indexOf(CHAT_STREAM_EVENT_SUFFIX, incompleteEventStart);
  if (suffixAfterStart === -1) {
    return withoutCompleteEvents.slice(0, incompleteEventStart);
  }

  return hideIncompleteTrailingTag(withoutCompleteEvents);
};

// Answers end with an emitter tag, which arrives in pieces: "[DOCUM", then "[DOCUMENTS:12,3", then the full tag.
// Hide the unfinished tail so it does not flash in the message while streaming.
const hideIncompleteTrailingTag = (text: string): string => {
  const start = text.lastIndexOf("[");
  if (start === -1) {
    return text;
  }

  const tail = text.slice(start);
  const isIncompleteTag = Object.values(EmitterDataTag).some((tag) => {
    const opening = `[${tag}:`;
    return opening.startsWith(tail) || (tail.startsWith(opening) && /^[-\w,]*$/.test(tail.slice(opening.length)));
  });

  return isIncompleteTag ? text.slice(0, start) : text;
};
